import {
  CATALOG_CAPABILITY_KEYS,
  type CatalogShelf,
  type ShelfInTeam,
  type ShelfKind,
  type ShelfRules,
  type ShelfSort,
} from "@mfa/v5-contracts";
import type { GalleryTemplate, SiteId } from "../../../core/types";

/**
 * The template filter bar's state, its URL format and its controls' values, kept free of
 * React so the gallery, the filter bar and the tests share one definition
 * (workflows/V5-Template-Organization.md Rev 2 §R2.2 "Filter bar", Rev 2.1 §R2.1.1 rows 4,
 * 11 and 12; workflows/V5-Template-Method-Review.md V1, V4, V7, L5). The rules that filter,
 * search, count and order the shelf live in contracts (`catalog-shelf.ts`, V6 plan row H.6),
 * so the server and any front-end list the same shelf; the site's shelf comes in the catalog
 * snapshot (content/shelves/<site>.yaml, V6 plan row H.24).
 *
 * - Every filter lives in the URL: `q`, `from` (mfa | lya), `kind` (team | single),
 *   `inTeam` (yes | no, read only with kind=single), `cat` (one category key), `ind`, `kw`
 *   (the Collections: authored keywords, Original among them) and `cap` (the Capabilities,
 *   derived), each a comma list, and the order in `sort` (title | newest). A `kw` link from
 *   before the split that names a capability still selects it.
 * - Search folds accents and case and needs every term (AND) somewhere in the title,
 *   description, category, industry, collection and capability labels, or a team's member
 *   names.
 * - Within a dimension the values are OR'd; across dimensions they are AND'd.
 * - Original is a collection, not a category (the template-method review, D1): an Original
 *   files under its topic and industries and carries the `original` keyword.
 * - Counts are recomputed against the other active filters, so a value that would empty
 *   the shelf reads 0 and is greyed, never hidden.
 * - Recommended order: the viewing site's own templates first, then the other site's; in
 *   each, the newer templates before the Originals, each group by title.
 * - One pinned blank per site leads the unfiltered shelf; every other blank is hidden
 *   from the gallery (its URL still opens). Blanks are counted under no filter.
 */

export type KindFilter = ShelfKind;
export type InTeamFilter = ShelfInTeam;
/** Both sites, or one home site. */
export type FromFilter = "all" | SiteId;
export type SortOrder = ShelfSort;

export const SITES_IN_ORDER: readonly SiteId[] = ["myfirstagent", "launchyouragent"];

/** The contracts module's `ShelfFilters`, with `from` narrowed to the sites and the order. */
export interface TemplateFilters {
  q: string;
  from: FromFilter;
  kind: KindFilter;
  inTeam: InTeamFilter;
  category: string | null;
  industries: string[];
  /** The Collections row: authored keywords (`tagKeys`), Original among them. */
  collections: string[];
  /** The Capabilities row: what the agent.yaml enables (`capabilityKeys`). */
  capabilities: string[];
  /** Not a filter: Clear filters keeps it. */
  sort: SortOrder;
}

export const EMPTY_FILTERS: TemplateFilters = {
  q: "",
  from: "all",
  kind: "all",
  inTeam: "any",
  category: null,
  industries: [],
  collections: [],
  capabilities: [],
  sort: "recommended",
};

const PARAM = {
  q: "q",
  from: "from",
  kind: "kind",
  inTeam: "inTeam",
  category: "cat",
  industries: "ind",
  collections: "kw",
  capabilities: "cap",
  sort: "sort",
} as const;

const CAPABILITY_KEYS: ReadonlySet<string> = new Set(CATALOG_CAPABILITY_KEYS);

/** The `from` values the URL carries, short and stable. */
const FROM_PARAM: Record<SiteId, string> = { myfirstagent: "mfa", launchyouragent: "lya" };

function readList(value: string | null): string[] {
  if (!value) return [];
  return [
    ...new Set(
      value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

/** The filters a URL holds; anything unknown or malformed reads as its default. */
export function readFilters(params: URLSearchParams): TemplateFilters {
  const kind = params.get(PARAM.kind);
  const inTeam = params.get(PARAM.inTeam);
  const category = params.get(PARAM.category)?.trim();
  const from = params.get(PARAM.from);
  const sort = params.get(PARAM.sort);
  // Before the Keywords row split in two, `kw` held capabilities too: those still select one.
  const keywords = readList(params.get(PARAM.collections));
  return {
    q: params.get(PARAM.q) ?? "",
    from: SITES_IN_ORDER.find((site) => FROM_PARAM[site] === from) ?? "all",
    kind: kind === "team" || kind === "single" ? kind : "all",
    // "In a team" only refines Single agents.
    inTeam: kind === "single" && (inTeam === "yes" || inTeam === "no") ? inTeam : "any",
    category: category ? category : null,
    industries: readList(params.get(PARAM.industries)),
    collections: keywords.filter((key) => !CAPABILITY_KEYS.has(key)),
    capabilities: [
      ...new Set([
        ...readList(params.get(PARAM.capabilities)),
        ...keywords.filter((key) => CAPABILITY_KEYS.has(key)),
      ]),
    ],
    sort: sort === "title" || sort === "newest" ? sort : "recommended",
  };
}

/**
 * `params` with the filter keys rewritten from `filters` (defaults are omitted, so a
 * clear bar is a bare /templates); any other query parameter is kept.
 */
export function writeFilters(params: URLSearchParams, filters: TemplateFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of Object.values(PARAM)) next.delete(key);
  if (filters.q !== "") next.set(PARAM.q, filters.q);
  if (filters.from !== "all") next.set(PARAM.from, FROM_PARAM[filters.from]);
  if (filters.kind !== "all") next.set(PARAM.kind, filters.kind);
  if (filters.kind === "single" && filters.inTeam !== "any") next.set(PARAM.inTeam, filters.inTeam);
  if (filters.category) next.set(PARAM.category, filters.category);
  if (filters.industries.length) next.set(PARAM.industries, filters.industries.join(","));
  if (filters.collections.length) next.set(PARAM.collections, filters.collections.join(","));
  if (filters.capabilities.length) next.set(PARAM.capabilities, filters.capabilities.join(","));
  if (filters.sort !== "recommended") next.set(PARAM.sort, filters.sort);
  return next;
}

export function hasActiveFilters(filters: TemplateFilters): boolean {
  return (
    filters.q.trim() !== "" ||
    filters.from !== "all" ||
    filters.kind !== "all" ||
    filters.category !== null ||
    filters.industries.length > 0 ||
    filters.collections.length > 0 ||
    filters.capabilities.length > 0
  );
}

/** Every filter off; the order stays as the viewer chose it. */
export function clearFilters(filters: TemplateFilters): TemplateFilters {
  return { ...EMPTY_FILTERS, sort: filters.sort };
}

/** Adds `value` to a multi-select list, or removes it when it is already there. */
export function toggleValue(list: readonly string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

// ---------------------------------------------------------------------------
// Rows and the site's shelf

/** The collections a template belongs to: its authored keywords (`tagKeys`). */
export const collectionsOf = (entry: GalleryTemplate) => entry.tagKeys ?? [];
/** What its agent.yaml enables (`capabilityKeys`). */
export const capabilitiesOf = (entry: GalleryTemplate) => entry.capabilityKeys ?? [];

/**
 * The site's shelf from the snapshot (a first-party shelf's id is its site id): what the
 * gallery lists, pins and ranks first. A snapshot without one (a server from before shelves)
 * lists every library, pins nothing, hides the blanks and ranks the site's own first.
 */
export function siteShelf(
  shelves: readonly CatalogShelf[] | undefined,
  siteId: string,
): ShelfRules {
  return (
    shelves?.find((shelf) => shelf.id === siteId) ?? {
      pinned: [],
      hideBlanks: true,
      rankFirst: { site: siteId },
    }
  );
}

// ---------------------------------------------------------------------------
// Order

/** Whether any row says when it went live: without that, "Newest" is not offered. */
export const canSortByNewest = (rows: readonly GalleryTemplate[]) =>
  rows.some((entry) => typeof entry.publishedAt === "string" && entry.publishedAt !== "");

// ---------------------------------------------------------------------------
// The controls' values

export interface FilterOption {
  key: string;
  label: string;
  /** A library-era category the vocabulary does not hold; it leaves once the backfill moves its rows. */
  legacy?: boolean;
}

/**
 * The Category row after "All": the vocabulary's topics in file order, then any key a row
 * carries that the vocabulary does not hold (the library-era start, pro, school, create…,
 * until the content backfill retires them, labelled from the library facets). Original is
 * not here: it is a collection (the Collections row).
 */
export function categoryOptions(
  vocabulary: readonly { key: string; label: string }[],
  rows: readonly GalleryTemplate[],
  labels: ReadonlyMap<string, string>,
): FilterOption[] {
  const known = new Set(vocabulary.map((term) => term.key));
  const topics = vocabulary.map((term) => ({ key: term.key, label: term.label }));
  const labelOrder = [...labels.keys()];
  const extra = [
    ...new Set(
      rows
        .map((entry) => entry.categoryKey)
        .filter((key): key is string => typeof key === "string" && !known.has(key)),
    ),
  ]
    .sort((a, b) => {
      const ia = labelOrder.indexOf(a);
      const ib = labelOrder.indexOf(b);
      if (ia !== ib)
        return (ia < 0 ? Number.MAX_SAFE_INTEGER : ia) - (ib < 0 ? Number.MAX_SAFE_INTEGER : ib);
      return a.localeCompare(b);
    })
    .map((key) => ({ key, label: labels.get(key) ?? key, legacy: true }));
  return [...topics, ...extra];
}

/**
 * A multi-select row's values: the vocabulary's terms in file order, then any key a row
 * carries that the vocabulary does not hold, labelled when a label is known.
 */
export function vocabularyOptions(
  vocabulary: readonly { key: string; label: string }[],
  rowKeys: Iterable<string>,
  labels: ReadonlyMap<string, string>,
): FilterOption[] {
  const known = new Set(vocabulary.map((term) => term.key));
  const extra = [...new Set(rowKeys)].filter((key) => !known.has(key)).sort();
  return [
    ...vocabulary.map((term) => ({ key: term.key, label: term.label })),
    ...extra.map((key) => ({ key, label: labels.get(key) ?? key })),
  ];
}
